import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { Button, ErrorState, Loading, PageHeader, Panel, StatusBadge, useConfirm, useToast } from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { formatDate, formatYen } from '@/lib/format';
import { useDeleteEquipment, useEquipment, type EquipmentDto } from './api';
import '@/features/vessels/vessels.css';

function Item({ label, wide, children }: { label: string; wide?: boolean; children?: ReactNode }) {
  return (
    <div className={wide ? 'bs-dl__item is-wide' : 'bs-dl__item'}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
const yen = (v: number | null) => (v == null ? null : formatYen(v));

/** S-81 機器管理 詳細 */
export function EquipmentDetailPage() {
  const { id } = useParams();
  const eqId = Number(id);
  const { data, isPending, error, refetch } = useEquipment(Number.isInteger(eqId) ? eqId : null);
  if (isPending) return <Loading block />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  return <Detail e={data} />;
}

function Detail({ e }: { e: EquipmentDto }) {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { yearPath } = useFiscalYear();
  const toast = useToast();
  const confirm = useConfirm();
  const del = useDeleteEquipment();
  const isAdmin = user.organizationType === '基金';

  const onDelete = async () => {
    const ok = await confirm({ title: '機器を削除しますか？', message: `管理番号 ${e.managementNo}（${e.category}）を削除します。この操作は取り消せません。`, tone: 'danger', confirmLabel: '削除する' });
    if (!ok) return;
    try {
      await del.mutateAsync(e.id);
      toast.success('機器を削除しました');
      navigate(yearPath('equipment'));
    } catch (err) {
      toast.error('削除できませんでした', errorMessage(err));
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="船舶・計画 ／ 機器管理"
        title={`管理番号 ${e.managementNo}`}
        meta={<StatusBadge tone="navy">{e.category}</StatusBadge>}
        description={`${e.businessOperatorName ?? ''}　${e.vesselName ?? e.nonParticipantVesselName ?? ''}`}
        actions={
          <>
            <Button icon={<ArrowLeft size={16} />} onClick={() => navigate(yearPath('equipment'))}>一覧へ戻る</Button>
            {isAdmin && (
              <>
                <Button variant="danger" icon={<Trash2 size={16} />} onClick={onDelete} loading={del.isPending}>削除</Button>
                <Button variant="primary" icon={<Pencil size={16} />} onClick={() => navigate(yearPath(`equipment/${e.id}/edit`))}>編集</Button>
              </>
            )}
          </>
        }
      />
      <Panel title="基本情報">
        <dl className="bs-dl">
          <Item label="事業実施者">{e.businessOperatorName}</Item>
          <Item label="機器管理番号"><span className="bs-num">{e.managementNo}</span></Item>
          <Item label="機器区分">{e.category}</Item>
          <Item label="年度">{e.fiscalYear ?? null}</Item>
          <Item label="識別番号"><span className="bs-num">{e.identificationNo}</span></Item>
          <Item label="メーカー・型番">{[e.maker, e.modelNo].filter(Boolean).join(' ／ ')}</Item>
        </dl>
      </Panel>
      <Panel title="船舶・使用者">
        <dl className="bs-dl">
          <Item label="参加船">{e.vesselName}</Item>
          <Item label="漁船登録番号"><span className="bs-num">{e.registrationNo}</span></Item>
          <Item label="トン数">{e.tonnage != null ? `${e.tonnage.toFixed(2)} トン` : null}</Item>
          <Item label="参加していない船名">{e.nonParticipantVesselName}</Item>
          <Item label="所有者">{e.ownerName}</Item>
          <Item label="使用者">{e.userName}</Item>
        </dl>
      </Panel>
      <Panel title="助成・処分制限">
        <dl className="bs-dl">
          <Item label="助成事業名">{e.subsidyProgramName}</Item>
          <Item label="耐用年数">{e.usefulLifeYears != null ? `${e.usefulLifeYears} 年` : null}</Item>
          <Item label="総事業費">{yen(e.totalCost)}</Item>
          <Item label="助成金">{yen(e.subsidyAmount)}</Item>
          <Item label="事業実施者負担金">{yen(e.operatorShare)}</Item>
          <Item label="処分制限年月日">{formatDate(e.disposalRestrictedUntil)}</Item>
          <Item label="処分承認年月日">{formatDate(e.disposalApprovedOn)}</Item>
          <Item label="処分内容" wide>{e.disposalDetail}</Item>
        </dl>
      </Panel>
      <Panel title="免許・貸出">
        <dl className="bs-dl">
          <Item label="免許の年月日">{formatDate(e.licensedOn)}</Item>
          <Item label="免許の有効期限">{formatDate(e.licenseExpiresOn)}</Item>
          <Item label="貸出開始日">{formatDate(e.lendingFrom)}</Item>
          <Item label="貸出終了日">{formatDate(e.lendingTo)}</Item>
          <Item label="備考" wide>{e.note}</Item>
        </dl>
      </Panel>
    </>
  );
}
